import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import ApplicationModal from './ApplicationModal';
import ApplicationPagesModal from './ApplicationPagesModal';
import apiClient from '../../assets/scripts/services/apiClient';
import authService from '../../assets/scripts/auth/authService';

const ApplicationManagement = () => {
  const [applications, setApplications] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pagesModalOpen, setPagesModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [modalTitle, setModalTitle] = useState('Create Application');
  const [organizationFilter, setOrganizationFilter] = useState('');

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  const checkAuthAndLoadData = async () => {
    const isAuthenticated = await authService.requireAuth();
    if (!isAuthenticated) {
      return;
    }
    await Promise.all([loadApplications(), loadOrganizations()]);
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get('/applications');
      console.log('Loaded applications list:', data);
      if (data && data.length > 0) {
        console.log('First application platform_communication_endpoints:', data[0].platform_communication_endpoints);
      }
      setApplications(data || []);
    } catch (err) {
      console.error('Error loading applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const data = await apiClient.get('/organizations');
      setOrganizations(data || []);
    } catch (err) {
      console.error('Error loading organizations:', err);
    }
  };

  const handleCreateApplication = () => {
    setSelectedApplication(null);
    setModalTitle('Create New Application');
    setModalOpen(true);
  };

  const handleEditApplication = (application) => {
    setSelectedApplication(application);
    setModalTitle('Edit Application');
    setModalOpen(true);
  };

  const handleDeleteApplication = async (applicationId) => {
    if (!confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.delete(`/applications/${applicationId}`);
      await loadApplications();
      showNotification('Application deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting application:', err);
      showNotification('Failed to delete application', 'error');
    }
  };

  const handleSaveApplication = async (formData) => {
    try {
      let response;
      
      if (selectedApplication) {
        // For updates, use the form data as the complete desired state
        // The form already contains all organizations the user wants for this application
        
        const updatedOrgAssociations = formData.organization_associations.map(assoc => ({
          organization_id: assoc.organization_id,
          user_id_access_list: assoc.user_id_access_list || []
        }));
        
        // PUT request with complete data - form data represents the complete desired state
        await apiClient.put(`/applications/${selectedApplication.id}`, {
          name: formData.name,
          description: formData.description || '',
          detail: formData.detail || {},
          organization_associations: updatedOrgAssociations,
          // Include optional sync fields
          application_sync_endpoint: formData.application_sync_endpoint || null,
          application_sync_api_key: formData.application_sync_api_key || null,
          // Service worker flag
          is_service_worker: formData.is_service_worker || false,
          // Platform communication endpoints
          platform_communication_endpoints: formData.platform_communication_endpoints || null
        });
        
        showNotification('Application updated successfully', 'success');
        response = { success: true };
      } else {
        // Create new application with organization_associations
        response = await apiClient.post('/applications', formData);
        
        // Store access key if provided (only on creation)
        if (response && response.access_key) {
          showNotification('Application created successfully! Access key has been displayed.', 'success');
        } else {
          showNotification('Application created successfully', 'success');
        }
      }
      
      if (!response || !response.access_key) {
        setModalOpen(false);
      }
      
      await loadApplications();
      return response; // Return response for access key handling
    } catch (err) {
      console.error('Error saving application:', err);
      throw err;
    }
  };

  const handleRegenerateApiKey = async (applicationId) => {
    if (!confirm('Are you sure you want to regenerate the API key? The old key will no longer work.')) {
      return;
    }

    try {
      await apiClient.post(`/applications/${applicationId}/regenerate-access-key`);
      await loadApplications();
      showNotification('API key regenerated successfully', 'success');
    } catch (err) {
      console.error('Error regenerating API key:', err);
      showNotification('Failed to regenerate API key', 'error');
    }
  };

  const handleRegisterPages = (application) => {
    setSelectedApplication(application);
    setPagesModalOpen(true);
  };

  const copyApiKey = async (apiKey) => {
    try {
      // Check if clipboard API is available
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(apiKey);
        showNotification('API key copied to clipboard!', 'success');
      } else {
        // Fallback method using a temporary textarea
        const textArea = document.createElement('textarea');
        textArea.value = apiKey;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            showNotification('API key copied to clipboard!', 'success');
          } else {
            throw new Error('Copy command failed');
          }
        } catch (err) {
          console.error('Fallback copy failed:', err);
          // Last resort: show the API key for manual copying
          prompt('Copy the API key below:', apiKey);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('Error copying API key:', err);
      // Show API key in prompt for manual copying
      prompt('Copy the API key below:', apiKey);
    }
  };

  const showNotification = (message, type = 'info') => {
    if (type === 'error') {
      alert('Error: ' + message);
    } else {
      alert('Success: ' + message);
    }
  };

  const filteredApplications = organizationFilter 
    ? applications.filter(app => {
        // Check both old and new structure
        if (app.organizations && app.organizations.length > 0) {
          return app.organizations.some(org => org.id === parseInt(organizationFilter));
        }
        return app.organization?.id === parseInt(organizationFilter);
      })
    : applications;

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="application-management">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="peers ai-c jc-sb fxw-nw mB-30">
              <div className="peer">
                <h4 className="c-grey-900 mT-10 mB-0">Application Management</h4>
              </div>
              <div className="peer">
                <button 
                  className="btn btn-primary" 
                  onClick={handleCreateApplication}
                >
                  <i className="ti-plus mR-5"></i>Create New Application
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="bgc-white bd bdrs-3 p-20 mB-20">
              <div className="row">
                <div className="col-md-4">
                  <label className="form-label">Filter by Organization</label>
                  <select 
                    className="form-control"
                    value={organizationFilter}
                    onChange={(e) => setOrganizationFilter(e.target.value)}
                  >
                    <option value="">All Organizations</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.detail?.org_name || `Organization ${org.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-12">
            <div className="bgc-white bd bdrs-3 p-20 mB-20">
              <h5 className="c-grey-900 mB-20">Applications</h5>
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Application Name</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th>API Key</th>
                      <th>Organization</th>
                      <th>Users</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No applications found. Create your first application to get started.
                        </td>
                      </tr>
                    ) : (
                      filteredApplications.map(application => (
                        <tr key={application.id}>
                          <td>{application.name}</td>
                          <td>
                            {application.is_service_worker ? (
                              <span className="badge bg-purple">
                                <i className="ti-settings me-1"></i>
                                Service Worker
                              </span>
                            ) : (
                              <span className="badge bg-primary">
                                <i className="ti-desktop me-1"></i>
                                Regular App
                              </span>
                            )}
                          </td>
                          <td>{application.description || 'N/A'}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <code className="me-2">
                                {application.access_key ? 
                                  `${application.access_key.substring(0, 8)}...` : 
                                  'N/A'
                                }
                              </code>
                              {application.access_key && (
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => copyApiKey(application.access_key)}
                                  title="Copy API Key"
                                >
                                  <i className="ti-clipboard"></i>
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            {application.is_service_worker ? (
                              <span className="badge bg-secondary">N/A - Service Worker</span>
                            ) : application.organizations && application.organizations.length > 0 ? (
                              <div>
                                {application.organizations.map((org, idx) => (
                                  <span key={org.id} className="badge bg-info me-1 mb-1">
                                    {org.name || `Org ${org.id}`}
                                  </span>
                                ))}
                                {application.organizations.length > 1 && (
                                  <div className="small text-muted mt-1">
                                    {application.organizations.length} organizations
                                  </div>
                                )}
                              </div>
                            ) : application.organization ? (
                              <span className="badge bg-info">
                                {application.organization.name || `Org ${application.organization.id}`}
                              </span>
                            ) : (
                              <span className="badge bg-secondary">No organization</span>
                            )}
                          </td>
                          <td>
                            {application.is_service_worker ? (
                              <span className="badge bg-secondary">N/A - Service Worker</span>
                            ) : application.organizations && application.organizations.length > 0 ? (
                              <div>
                                {application.organizations.map((org, idx) => {
                                  const userCount = org.users ? org.users.length : 0;
                                  return (
                                    <div key={org.id} className="mb-1">
                                      <span className="badge bg-primary">
                                        {userCount === 0 ? 'Full Access' : `${userCount} user${userCount !== 1 ? 's' : ''}`}
                                      </span>
                                      {userCount > 0 && (
                                        <div className="small text-muted">
                                          {org.users.slice(0, 2).map(u => u.name || u.id).join(', ')}
                                          {userCount > 2 && '...'}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : application.users && application.users.length > 0 ? (
                              <div>
                                <span className="badge bg-primary">
                                  {application.users.length} user{application.users.length !== 1 ? 's' : ''}
                                </span>
                                <div className="small text-muted mt-1">
                                  {application.users.slice(0, 3).map(u => u.name).join(', ')}
                                  {application.users.length > 3 && '...'}
                                </div>
                              </div>
                            ) : (
                              <span className="badge bg-secondary">No specific users</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${application.is_active ? 'bg-success' : 'bg-secondary'}`}>
                              {application.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <button 
                              className="btn btn-sm btn-secondary me-2"
                              onClick={() => handleEditApplication(application)}
                              title="Edit Application"
                            >
                              <i className="ti-pencil"></i> Edit
                            </button>
                            {!application.is_service_worker && (
                              <button 
                                className="btn btn-sm btn-info me-2"
                                onClick={() => handleRegisterPages(application)}
                                title="Register Application Pages"
                              >
                                <i className="ti-files"></i> Register Pages
                              </button>
                            )}
                            <button 
                              className="btn btn-sm btn-warning me-2"
                              onClick={() => handleRegenerateApiKey(application.id)}
                              title="Regenerate API Key"
                            >
                              <i className="ti-refresh"></i> Regenerate Key
                            </button>
                            <button 
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteApplication(application.id)}
                              title="Delete Application"
                            >
                              <i className="ti-trash"></i> Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ApplicationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveApplication}
        application={selectedApplication}
        title={modalTitle}
        organizations={organizations}
      />

      <ApplicationPagesModal
        isOpen={pagesModalOpen}
        onClose={() => setPagesModalOpen(false)}
        application={selectedApplication}
      />
    </div>
  );
};

export default ApplicationManagement;

export const initApplicationManagement = () => {
  const mountElement = document.getElementById('application-management-root');
  if (mountElement) {
    const root = ReactDOM.createRoot(mountElement);
    root.render(<ApplicationManagement />);
  }
};
